import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [coach, setCoach] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('accessToken'));

  // Set auth header on token change
  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCoachProfile();
    } else {
      delete api.defaults.headers.common['Authorization'];
      setCoach(null);
      setLoading(false);
    }
  }, [token]);

  const fetchCoachProfile = async () => {
    try {
      const response = await api.get('/coach/me');
      setCoach(response.data);
    } catch (error) {
      console.error('Failed to fetch coach profile:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { access_token, refresh_token } = response.data;
    localStorage.setItem('accessToken', access_token);
    localStorage.setItem('refreshToken', refresh_token);
    setToken(access_token);
    return response.data;
  };

  const register = async (email, password, name, specialty) => {
    const response = await api.post('/auth/register', { email, password, name, specialty });
    const { access_token, refresh_token } = response.data;
    localStorage.setItem('accessToken', access_token);
    localStorage.setItem('refreshToken', refresh_token);
    setToken(access_token);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setToken(null);
    setCoach(null);
  };

  const value = {
    coach,
    loading,
    isAuthenticated: !!token && !!coach,
    login,
    register,
    logout,
    refreshProfile: fetchCoachProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// API Functions
export const apiClient = api;

// Coach API
export const coachAPI = {
  getProfile: () => api.get('/coach/me'),
  updateProfile: (data) => api.put('/coach/me', data),
  getUsage: () => api.get('/coach/me/usage'),
};

// Clients API
export const clientsAPI = {
  list: (params) => api.get('/clients', { params }),
  create: (data) => api.post('/clients', data),
  get: (id) => api.get(`/clients/${id}`),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  regenerateToken: (id) => api.post(`/clients/${id}/regenerate-token`),
};

// Measurements API
export const measurementsAPI = {
  list: (clientId) => api.get(`/clients/${clientId}/measurements`),
  create: (clientId, data) => api.post(`/clients/${clientId}/measurements`, data),
  get: (clientId, measurementId) => api.get(`/clients/${clientId}/measurements/${measurementId}`),
  update: (clientId, measurementId, data) => api.put(`/clients/${clientId}/measurements/${measurementId}`, data),
  delete: (clientId, measurementId) => api.delete(`/clients/${clientId}/measurements/${measurementId}`),
  getTrends: (clientId) => api.get(`/clients/${clientId}/trends`),
};

// Photos API
export const photosAPI = {
  list: (clientId, measurementId) => api.get(`/clients/${clientId}/measurements/${measurementId}/photos`),
  upload: (clientId, measurementId, file, category) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);
    return api.post(`/clients/${clientId}/measurements/${measurementId}/photos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  delete: (clientId, measurementId, photoId) => api.delete(`/clients/${clientId}/measurements/${measurementId}/photos/${photoId}`),
  listAll: (clientId) => api.get(`/clients/${clientId}/photos`),
};

// Consultations API
export const consultationsAPI = {
  list: (clientId, params) => api.get(`/clients/${clientId}/consultations`, { params }),
  create: (clientId, data) => api.post(`/clients/${clientId}/consultations`, data),
  get: (clientId, consultationId) => api.get(`/clients/${clientId}/consultations/${consultationId}`),
  analyze: (clientId, consultationId, data) => api.post(`/clients/${clientId}/consultations/${consultationId}/analyze`, data),
  delete: (clientId, consultationId) => api.delete(`/clients/${clientId}/consultations/${consultationId}`),
};

// Feedback API
export const feedbackAPI = {
  create: (clientId, measurementId, data) => api.post(`/clients/${clientId}/measurements/${measurementId}/feedback`, data),
  update: (clientId, measurementId, feedbackId, data) => api.put(`/clients/${clientId}/measurements/${measurementId}/feedback/${feedbackId}`, data),
};

// Public API
export const publicAPI = {
  getClient: (accessToken) => api.get(`/public/client/${accessToken}`),
  getProgress: (accessToken) => api.get(`/public/client/${accessToken}/progress`),
};

export default api;
